import React, { useEffect, useState } from "react";
import {api} from "../Login/axiosConfig";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Header1 from "../Header/Header1";
import Footer2 from "../Footer/Footer2";

const API_URL = process.env.REACT_APP_API_URL;

const aiPlans = [
  {
    key: "AI_MONTHLY",
    title: "AI Monthly",
    price: "₹150",
    duration: "per month",
    description: "Perfect for getting started with AI learning",
    features: [
      "Access to recordings, resources & materials",
      "Access to professional AI community",
      "24×7 mentor support",
      "Hands-on practical workshops",
      "Certification on completion",
    ],
    buttonText: "Get Started",
  },

  {
    key: "AI_6_MONTHS",
    title: "AI 6 Months",
    price: "₹800",
    duration: "for 6 months",
    description: "Build strong AI skills with guided practical learning",
    features: [
      "Everything in Monthly plan",
      "Custom on-demand AI sessions",
      "Advanced practical workshops",
      "Priority mentor guidance",
      "Real-world project opportunities",
      "Access to exclusive learning resources",
      "Certification included",
    ],
    buttonText: "Join Now",
  },

  {
    key: "AI_ANNUAL",
    title: "AI Annual",
    price: "₹1500",
    duration: "per year",
    description: "Complete AI growth program with career-focused opportunities",
    features: [
      "Everything in 6 Months plan",
      "Real-world project opportunities with compensation",
      "Premium AI community access",
      "Career-focused mentorship",
      "Exclusive AI sessions & workshops",
      "24×7 dedicated mentor support",
      "Lifetime membership eligibility (limited to first 1000 annual members)",
      "Professional certification",
    ],
    buttonText: "Become a Member",
  },
];

const plans = [
  {
    key: "FREE",
    title: "Free Pass",
    price: "₹0",
    duration: "Forever Free",
    description: "Get a taste of live & on-demand learning",
    features: [
      "Access to selected live sessions",
      "Limited on-demand replays",
      "Community discussions",
    ],
    buttonText: "Start Watching",
  },
  {
    key: "MONTHLY",
    title: "Monthly",
    price: "₹49",
    duration: "per month",
    description: "Unlimited learning, billed monthly",
    features: [
      "Unlimited live sessions",
      "Full on-demand library",
      "Session replays",
      "Learning insights",
      "Email support",
    ],
    buttonText: "Subscribe Monthly",
  },
  {
    key: "QUARTERLY",
    title: "Quarterly",
    price: "₹129",
    duration: "billed every 3 months",
    description: "More value for committed learners",
    features: [
      "Unlimited live sessions",
      "Complete on-demand access",
      "Downloadable resources",
      "Priority support",
      "Live Q&A with experts",
    ],
    buttonText: "Choose Quarterly",
  },
  {
    key: "ANNUAL",
    title: "Annual",
    price: "₹499",
    duration: "per year",
    description: "Best value for serious learners",
    features: [
      "All live & on-demand sessions",
      "Early access to new content",
      "Premium learning insights",
      "Dedicated support",
      "Exclusive expert sessions",
    ],
    buttonText: "Go Annual",
  },
];

const SubscriptionPage = () => {
  const { userId } = useAuth();
  const navigate = useNavigate();
  const [subscriptionType, setSubscriptionType] = useState("platform");
  const displayPlans = subscriptionType === "platform" ? plans : aiPlans;

  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);

  useEffect(() => {
    if (!userId) return;

    const fetchSubscription = async () => {
      try {
        await api.post(`${API_URL}/subscriptions/init/`, {
          user_id: userId,
        });

        const res = await api.post(
          `${API_URL}/subscriptions/get/`,
          { user_id: userId }
        );

        setCurrentPlan(res.data[subscriptionType]?.current_plan);
      } catch (error) {
        console.error("Subscription fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [userId]);

  const handleSubscribe = async (planKey) => {
    if (planKey === "FREE") return;

    try {
      setProcessingPlan(planKey);

      const res = await api.post(
        `${API_URL}/subscriptions/upgrade/`,
        {
          user_id: userId,
          target_plan: planKey,
          subscription_type: subscriptionType, 
        }
      );

      navigate("/new-subscription-payments", {
        state: res.data,
      });
    } catch (error) {
      console.error("Upgrade error:", error);
    } finally {
      setProcessingPlan(null);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          bgcolor: "#000",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ color: "#fff" }}>
          Loading subscription…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#000", minHeight: "100vh" }}>
      <Header1 />

  

      {/* HERO */}
      <Box
        sx={{
          py: 10,
          textAlign: "center",
          background:
            "radial-gradient(circle at top, rgba(25,118,210,0.2), transparent 65%)",
        }}
      >
        <Typography variant="h3" fontWeight="800" sx={{ color: "#fff", mb: 1 }}>
          Learn Live. Stream On-Demand.
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.7)" }}>
          Your knowledge platform — live classes, expert sessions & replays.
        </Typography>
      </Box>

          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 5 }}>
  <Button
    variant={subscriptionType === "platform" ? "contained" : "outlined"}
    onClick={() => setSubscriptionType("platform")}
  >
    Platform Plans
  </Button>

  <Button
    variant={subscriptionType === "ai" ? "contained" : "outlined"}
    onClick={() => setSubscriptionType("ai")}
  >
    AI Membership
  </Button>
</Box>


      {/* PRICING */}
      <Container maxWidth="lg" sx={{ pb: 10 }}>
        <Grid container spacing={4} justifyContent="center">
          {displayPlans.map((plan) => {
            const isCurrent = plan.key === currentPlan;

            return (

              <Grid
                item
                key={plan.key}
                xs={12}
                sm={6}
                md={3}
                display="flex"
                justifyContent="center"
              >
                <Card
                  sx={{
                    width: "100%",
                    maxWidth: 320,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    background: isCurrent
                      ? "rgba(25,118,210,0.18)"
                      : "rgba(255,255,255,0.06)",
                    backdropFilter: "blur(18px)",
                    borderRadius: 4,
                    border: isCurrent
                      ? "2px solid #1976d2"
                      : "1px solid rgba(255,255,255,0.12)",
                  }}
                >
                  {isCurrent && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 16,
                        left: "50%",
                        transform: "translateX(-50%)",
                        px: 2,
                        py: 0.5,
                        borderRadius: 2,
                        fontSize: 12,
                        fontWeight: "bold",
                        color: "#fff",
                        background:
                          "linear-gradient(135deg, #1976d2, #42a5f5)",
                      }}
                    >
                      Current Plan
                    </Box>
                  )}

                  <CardContent sx={{ p: 4, flexGrow: 1 }}>
                    <Typography variant="h5" fontWeight="700" sx={{ color: "#fff" }}>
                      {plan.title}
                    </Typography>

                    <Typography
                      variant="h3"
                      fontWeight="800"
                      sx={{ mt: 2, color: "#1976d2" }}
                    >
                      {plan.price}
                    </Typography>

                    <Typography sx={{ color: "rgba(255,255,255,0.6)", mb: 2 }}>
                      {plan.duration}
                    </Typography>

                    <Typography sx={{ color: "rgba(255,255,255,0.75)", mb: 3 }}>
                      {plan.description}
                    </Typography>

                    {plan.features.map((feature, i) => (
                      <Box key={i} display="flex" gap={1} mb={1}>
                        <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: "#fff" }}>
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>

                  <CardActions sx={{ px: 4, pb: 4 }}>
                    <Button
                      fullWidth
                      size="large"
                      disabled={isCurrent || processingPlan === plan.key}
                      variant={isCurrent ? "contained" : "outlined"}
                      onClick={() => handleSubscribe(plan.key)}
                      sx={{
                        py: 1.4,
                        borderRadius: 3,
                        fontWeight: "bold",
                        textTransform: "none",
                      }}
                    >
                      {isCurrent
                        ? "Current Plan"
                        : processingPlan === plan.key
                        ? "Processing..."
                        : plan.buttonText}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Box textAlign="center" mt={6}>
          <Typography sx={{ color: "rgba(255,255,255,0.6)" }}>
            Cancel anytime • Watch anywhere • Learn at your pace
          </Typography>
        </Box>
      </Container>

      <Footer2 />
    </Box>
  );
};

export default SubscriptionPage;
